import { jwtDecode } from "jwt-decode";
import { useAuth } from "./useAuth";
import { pathAfterAuth } from "./after-auth";
import { linkPendingGuest } from "./guest-share";
import { refreshDatasourcesAfterGuestLink } from "./refresh-datasources";

/**
 * Adopt an OnlyDash JWT minted by one of the server-side sign-in flows and
 * return the path to land on.
 *
 * Both providers that mint a token outside the useAuth store go through here:
 * the OAuth redirect callback, which reads it off the query string, and the
 * Firebase popup, which gets it back from POST /auth/firebase. Rehydrating is
 * what pushes the new state into localStorage, and skipping it leaves the store
 * looking signed in until the next reload throws it away.
 */
export async function adoptAuthToken(token: string): Promise<string> {
  const decoded = jwtDecode<{
    user?: unknown;
    guest?: unknown;
    exp?: number;
  }>(token);
  if (typeof decoded.exp === "number" && decoded.exp * 1000 < Date.now()) {
    throw new Error("expired");
  }
  const link = await linkPendingGuest(token);
  useAuth.setState({
    token,
    user: decoded.user || null,
    guest: decoded.guest || null,
    isAuthenticated: true,
    error: null,
  });
  await useAuth.persist.rehydrate();
  await refreshDatasourcesAfterGuestLink(link);
  if (link?.ok && link.returnPath) return link.returnPath;
  return pathAfterAuth(token);
}
